/**
 * HR's review queue.
 *
 * Two tabs, because they are two different jobs. **To review** is work waiting on a decision,
 * oldest first — aging visible, because a proposal sitting for three weeks is the thing this
 * app exists to stop. **Overdue** lists `claim_overdue` proposals whose reservations are still
 * locked: only HR can release them (D-36), and nothing else will, so if this tab is not looked
 * at the money stays reserved forever.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { Deadline, Money, PageHeader, ProposalStatusBadge, RefLink } from '@/components/bits';
import { Alert, Badge, Card, EmptyState, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { daysBetween, formatDate, sgtToday } from '@/lib/dates';
import type { ProposalStatus } from '@/lib/types';

const TABS: Array<{ key: string; label: string; statuses: ProposalStatus[] }> = [
  { key: 'review', label: 'To review', statuses: ['hr_review', 'clarification_requested'] },
  { key: 'sponsor', label: 'With Sponsors', statuses: ['sponsor_review'] },
  { key: 'overdue', label: 'Overdue — holding budget', statuses: ['claim_overdue'] },
  { key: 'confirmed', label: 'Confirmed', statuses: ['confirmed', 'claim_due'] },
];

export function HrReview() {
  const session = useSession();
  const [tab, setTab] = useState('review');
  const active = TABS.find((entry) => entry.key === tab) ?? TABS[0];

  const proposals = useQuery({
    queryKey: ['proposals', 'hr', session.year, tab],
    queryFn: () =>
      api.proposals({ budget_year: session.year, status: active?.statuses, limit: 200 }),
  });

  const items = [...(proposals.data?.items ?? [])].sort((a, b) =>
    (a.submitted_at ?? a.updated_at).localeCompare(b.submitted_at ?? b.updated_at),
  );
  const today = sgtToday();

  return (
    <>
      <PageHeader
        title="HR review"
        subtitle={`${session.year} · oldest first, so nothing quietly ages`}
      />

      <div className="mb-4 flex flex-wrap gap-1" role="tablist" aria-label="Review queues">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            onClick={() => setTab(entry.key)}
            className={
              tab === entry.key
                ? 'bg-secondary rounded-md px-3 py-1.5 text-sm font-medium'
                : 'text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-sm'
            }
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'overdue' && items.length > 0 ? (
        <Alert tone="warn" title="These reservations are still held" className="mb-4">
          An overdue event keeps its reservation until HR closes it out. That is deliberate —
          nothing is written off automatically — but it means the budget stays unavailable to the
          department until someone acts here.
        </Alert>
      ) : null}

      <Card title={active?.label ?? 'Queue'}>
        {proposals.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState title="Nothing in this queue">
            {tab === 'overdue'
              ? 'No confirmed event has passed its claim window unclaimed.'
              : 'Nothing is waiting here.'}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Event</Th>
                <Th>Department</Th>
                <Th>Event date</Th>
                <Th numeric>People</Th>
                <Th numeric>Requested</Th>
                <Th numeric>Pool</Th>
                <Th>{tab === 'overdue' ? 'Overdue since' : 'Waiting'}</Th>
                <Th>Stage</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const waitingDays = daysBetween(
                  (item.submitted_at ?? item.updated_at).slice(0, 10),
                  today,
                );
                return (
                  <tr key={item.id}>
                    <Td>
                      <RefLink to={`/proposals/${item.id}`} refNo={item.ref_no} />
                    </Td>
                    <Td>
                      {item.activity_title}
                      <p className="text-muted-foreground text-xs">
                        {activityLabel(item.activity_type)}
                      </p>
                    </Td>
                    <Td className="text-xs">{item.department_name}</Td>
                    <Td className="whitespace-nowrap">{formatDate(item.proposed_date)}</Td>
                    <Td numeric>
                      {item.attendee_count ?? '—'}
                      {item.participation_pct === null ? null : (
                        <span className="text-muted-foreground ml-1 text-xs">
                          {Math.round(item.participation_pct)}%
                        </span>
                      )}
                    </Td>
                    <Td numeric>
                      <Money cents={item.requested_amount_cents} />
                    </Td>
                    <Td numeric>
                      <Money cents={item.computed_pool_cents} muted />
                    </Td>
                    <Td>
                      {tab === 'overdue' ? (
                        <Deadline date={item.claim_deadline} />
                      ) : (
                        <span
                          className={
                            waitingDays >= 7 ? 'text-[var(--color-warning)] text-sm' : 'text-sm'
                          }
                        >
                          {waitingDays === 0
                            ? 'today'
                            : `${waitingDays} ${waitingDays === 1 ? 'day' : 'days'}`}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex flex-col items-start gap-1">
                        <ProposalStatusBadge status={item.status} />
                        {item.pending_exceptions > 0 ? (
                          <Badge tone="warn">{item.pending_exceptions} to decide</Badge>
                        ) : null}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
